import React from 'react';
import { Box } from '@ascension/web';
import { DEFAULT_SELECTION_NONE } from '@utilities/constants';
import { StatefulSelect } from '@components/StatefulInputs/StatefulSelect/StatefulSelect';
import { useGetSurgeonOptionsByPractice } from '@utilities/hooks/useGetSurgeonOptionsByPractice/useGetSurgeonOptionsByPractice';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import getRequestFormLabelStyle from '@components/RequestForm/utilities/getRequestFormLabelStyle';
import { Add as AddIcon } from '@material-ui/icons';
import { useFormContext, UseFormSetValue } from 'react-hook-form';
import { SurgeryProcedureInterface } from '@components/RequestForm/SurgeryInformation/SurgeryInformationInterface';
import { SelectOptionExtended } from '@components/StatefulInputs/StatefulSelect/StatefulSelect.types';

const useStyles = makeStyles((theme) =>
	createStyles({
		root: {
			'paddingBottom': theme.spacing(2),
			'width': '100%',

			'& .MuiOutlinedInput-root': {
				height: '52px',
			},

			'& .MuiInputLabel-root': {
				...getRequestFormLabelStyle(theme),
				paddingBottom: 0,
				textTransform: 'uppercase'
			},
		},
		customSelection: {
			'display': 'flex',
			'alignItems': 'center',
			'fontFamily': 'inherit',
			'& svg': {
				marginRight: theme.spacing(1),
			},
		},
		selectPopover: {
			'height': '400px',
			'& .MuiList-padding': {
				paddingBottom: 0,
			},
			'& .MuiMenuItem-root:last-child': {
				'position': 'sticky',
				'bottom': 0,
				'backgroundColor': theme.palette.common.white,
				'color': theme.palette.primary.main,
				'zIndex': 1,
				'borderTop': `1px solid ${theme.palette.grey[300]}`,
				'& svg': {
					color: theme.palette.primary.main,
				},
			},
		},
	}));

interface SecondarySurgeonSelectProps {
	dataField: string;
}

const CUSTOM_SURGEON_VALUE = 'CUSTOM_SURGEON';

export const handleSecondarySurgeonChangeEvent = (
	optionsWithNone: SelectOptionExtended[],
	setValue: UseFormSetValue<SurgeryProcedureInterface>
) => (e: React.ChangeEvent<{ value: string | null }>) => {
	handleSecondarySurgeonChange(e, optionsWithNone, setValue)
};

export const handleSecondarySurgeonChange = (
	event: React.ChangeEvent<{ value: string | null }>,
	optionsWithNone: SelectOptionExtended[],
	setValue: UseFormSetValue<SurgeryProcedureInterface>
) => {
	const value = event.target.value;
	const rawLabel = optionsWithNone.find((item) => item.value === value)?.label;
	const name = typeof rawLabel === 'string' ? rawLabel : '';

	if (value === CUSTOM_SURGEON_VALUE) {
		setValue('secondarySurgeon', { id: null, name: CUSTOM_SURGEON_VALUE, isCustom: true });
		setValue('customSecondarySurgeon', '', { shouldValidate: true });
		return;
	};

	setValue('secondarySurgeon', { id: value, name: name, isCustom: false });
};

export const StickyLabel = ({ className }: { className: string; }) => (
	<Box className={className}>
		<AddIcon />
		Custom Surgeon Name (not in current system)
	</Box>
);

export const SecondarySurgeonSelect = ({ dataField }: SecondarySurgeonSelectProps) => {
	const classes = useStyles();
	const options = useGetSurgeonOptionsByPractice();
	const { watch, setValue } = useFormContext<SurgeryProcedureInterface>();
	const secondarySurgeon = watch('secondarySurgeon');

	const optionsWithNone = React.useMemo(() => {
		const noneOption = {
			label: 'None',
			value: DEFAULT_SELECTION_NONE,
		};

		const customOption = {
			label: <StickyLabel className={classes.customSelection} />,
			value: CUSTOM_SURGEON_VALUE,
		};

		return [ noneOption, ...options, customOption]
	}, [options]);

	const controllerOptions = {
		shouldUnregister: true,
		rules: {
			onChange: handleSecondarySurgeonChangeEvent(optionsWithNone, setValue),
		},
	};

	const surgeonValue = React.useMemo(() => {
		if (secondarySurgeon?.isCustom) {
			return CUSTOM_SURGEON_VALUE;
		}
		return secondarySurgeon?.id || DEFAULT_SELECTION_NONE;
	}, [secondarySurgeon]);

	return (
		<>
			<StatefulSelect<SurgeryProcedureInterface, SelectOptionExtended>
				dataField={dataField}
				name="secondarySurgeon"
				value={surgeonValue}
				placeholder="Select an option"
				options={optionsWithNone}
				label="Secondary Surgeon"
				controllerOptions={controllerOptions}
				wrapperClassName={classes.root}
				MenuProps={{
					PaperProps: {
						className: classes.selectPopover,
					},
				}}
			/>
		</>
	);
};
